/**
 * Решения владельца на экране чистки комментариев (SPEC-055).
 *
 * Вынесено из компонента отдельным модулем: это единственное место, где решение
 * превращается в то, что уйдёт на сервер и переживёт применение, — и его надо
 * тестировать без DOM. Компонент остаётся тонким.
 */

export type ClusterVerdict =
    | { kind: "pending" }
    | { kind: "accept"; target: string }        // слить в предложенный или исправленный владельцем канон
    | { kind: "reject" };                       // отказ запоминается навсегда

export type JunkVerdict =
    | { kind: "pending" }
    | { kind: "keep" }                          // осмысленное значение, не трогаем
    | { kind: "clear" }                         // стереть комментарий у этих трат
    | { kind: "rewrite"; target: string };      // переписать своим текстом

export interface DecisionState {
    clusters: Record<string, ClusterVerdict>;   // ключ — исходное значение (member.note)
    junk: Record<string, JunkVerdict>;          // ключ — исходное значение
}

export const emptyDecisions: DecisionState = { clusters: {}, junk: {} };

export interface ApplyPayload {
    run_id: string;
    accepted: Array<{ source: string; target: string; kind: "merge" | "rewrite" | "clear" }>;
    rejected: Array<{ source: string; target: string }>;
}

export function setCluster(state: DecisionState, source: string, verdict: ClusterVerdict): DecisionState {
    return { ...state, clusters: { ...state.clusters, [source]: verdict } };
}

export function setJunk(state: DecisionState, source: string, verdict: JunkVerdict): DecisionState {
    return { ...state, junk: { ...state.junk, [source]: verdict } };
}

/** Сколько решений принято — кнопка «Применить» бессмысленна, пока их нет. */
export function decidedCount(state: DecisionState): number {
    const c = Object.values(state.clusters).filter((v) => v.kind !== "pending").length;
    const j = Object.values(state.junk).filter((v) => v.kind !== "pending" && v.kind !== "keep").length;
    return c + j;
}

/**
 * Payload для `POST /v1/web/note-cleanup/apply`.
 *
 * `defaultTargets` — предложенный сервером канон для каждого варианта: нужен,
 * чтобы отказ («не сливать») сохранился именно той парой, которую владелец видел.
 * «Оставить» для сомнительного значения не отправляется вообще: это решение
 * «ничего не делать», а не правка.
 */
export function buildApplyPayload(
    state: DecisionState,
    runId: string,
    defaultTargets: Record<string, string>,
): ApplyPayload {
    const accepted: ApplyPayload["accepted"] = [];
    const rejected: ApplyPayload["rejected"] = [];

    for (const [source, v] of Object.entries(state.clusters)) {
        if (v.kind === "accept" && v.target.trim()) accepted.push({ source, target: v.target.trim(), kind: "merge" });
        else if (v.kind === "reject") rejected.push({ source, target: defaultTargets[source] ?? source });
    }
    for (const [source, v] of Object.entries(state.junk)) {
        if (v.kind === "clear") accepted.push({ source, target: "", kind: "clear" });
        else if (v.kind === "rewrite" && v.target.trim()) accepted.push({ source, target: v.target.trim(), kind: "rewrite" });
    }
    return { run_id: runId, accepted, rejected };
}
